import React, { useState, useEffect } from 'react';
import api, { visiteAPI } from '../services/api';

/**
 * RiferimentoSelect — a cosa punta un promemoria o un compito.
 *
 * PERCHE'
 * Un avviso che dice solo "paziente Rossi" costringe chi lo legge a
 * ricordarsi di cosa si trattava: quale visita, quale consenso. Qui si
 * dichiara la destinazione una volta, quando si scrive l'avviso e il
 * contesto e' ancora fresco, e chi lo apre ci arriva con un clic.
 *
 * UNDICI TIPI, NON PIU' TRE
 * Il database ne ammette undici (dominio `tipo_riferimento`, migration
 * 115) ma qui se ne potevano scegliere tre piu' le visite. Gli altri
 * funzionavano solo se scritti da un programma - il giro notturno del
 * Sistema TS, l'avviso di autorizzazione - e chi scriveva un promemoria a
 * mano non li poteva indicare. Meta' meccanismo.
 *
 * DUE MENU, NON UNO SOLO PIU' LUNGO
 * Prima il tipo, poi - solo per i tipi che indicano UNA riga precisa -
 * quale. Un menu solo avrebbe dovuto contenere tutte le visite, tutti i
 * documenti, tutti i lotti e tutte le fatture insieme: illeggibile, e
 * sette richieste al server a ogni apertura del modulo. Cosi' invece si
 * carica un elenco solo, quando serve.
 *
 * DUE FAMIGLIE
 * I tipi clinici stanno dentro la scheda di un paziente e compaiono solo
 * quando un paziente e' stato scelto. Quelli di struttura - lotti,
 * Sistema TS - non hanno paziente e ci sono sempre: e' la ragione per cui
 * questo componente non torna piu' `null` quando il paziente manca.
 *
 * LA RISERVATEZZA VALE ANCHE QUI
 * Ogni elenco passa dall'endpoint normale, con i permessi di sempre: chi
 * non puo' vedere una procedura estetica non se la trova fra le
 * destinazioni, e chi non vede il magazzino non vede i lotti.
 *
 * Props:
 *  - pazienteId    null finche' non e' stato scelto un paziente
 *  - tipo, id      valore corrente
 *  - onChange      (tipo, id) => void, entrambi null per "nessuna"
 */

const dataIta = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
};

/* Come si riconosce una visita in un elenco a tendina: le prestazioni
   eseguite, che e' il modo in cui chi lavora la ricorda. Se non ce ne
   sono resta la patologia, e in ultimo solo la data. */
const etichettaVisita = (v) => {
    const nomi = (v.prestazioni || []).map((p) => p.nome_snapshot).filter(Boolean);
    if (nomi.length) return nomi.slice(0, 2).join(', ') + (nomi.length > 2 ? '…' : '');
    return v.patologia || v.trattamento || '';
};

/* Numero e anno sono NULL finche' la fattura e' una bozza: senza questo
   l'elenco mostrava «null/null». Una bozza si riconosce dall'intestatario
   e basta - il numero non ce l'ha ancora per definizione. */
const etichettaFattura = (f) => {
    const num = f.numero ? `${f.numero}/${f.anno}` : 'bozza';
    return [num, f.intestatario].filter(Boolean).join(' — ');
};

const riga = (id, etichetta) => ({ id, etichetta });

/* I tipi ammessi dal database, con come si chiamano a schermo e - per
   quelli che indicano una riga - dove si va a prendere l'elenco.

   Va tenuto allineato al dominio `tipo_riferimento`: un tipo che il
   database ammette e qui non c'e' semplicemente non si puo' scegliere,
   e uno che c'e' qui ma il database non ammette viene rifiutato al
   salvataggio con «Destinazione non riconosciuta». */
const TIPI = [
    // --- dentro la scheda del paziente ---
    {
        tipo: 'visita', etichetta: 'Una visita', clinico: true, vuoleId: true,
        carica: (pz) => visiteAPI.getByPazienteId(pz).then((r) =>
            (r.data?.data?.visite || []).map((v) => riga(
                v.id,
                [dataIta(v.data_visita), etichettaVisita(v)].filter(Boolean).join(' — ')))),
    },
    { tipo: 'consensi', etichetta: 'Consensi e documenti', clinico: true },
    {
        tipo: 'documento', etichetta: 'Un documento preciso', clinico: true, vuoleId: true,
        carica: (pz) => api.get(`/modulistica/paziente/${pz}`).then((r) =>
            (r.data?.data?.documenti || []).map((d) => riga(
                d.id, [d.tipo, d.titolo].filter(Boolean).join(' — ').slice(0, 70)))),
    },
    { tipo: 'foto', etichetta: 'Le foto', clinico: true },
    { tipo: 'anamnesi', etichetta: "L'anamnesi", clinico: true },
    {
        tipo: 'fattura', etichetta: 'Una fattura', clinico: true, vuoleId: true,
        /* Le ultime cinquanta, non quelle di questo paziente: la fattura
           e' intestata a un soggetto di fatturazione, e risalire al
           paziente vuole due join che qui non si possono fare. Chi scrive
           un promemoria su una fattura intende quasi sempre una recente. */
        carica: () => api.get('/fatturazione?per_pagina=50').then((r) =>
            (r.data?.data?.fatture || []).map((f) => riga(f.id, etichettaFattura(f)))),
        nota: 'Le ultime 50 emesse.',
    },
    {
        tipo: 'compilazione', etichetta: 'Una scheda compilata', clinico: true, vuoleId: true,
        carica: (pz) => api.get(`/questionari/paziente/${pz}`).then((r) =>
            (r.data?.data?.compilazioni || []).map((c) => riga(
                c.id, `${c.sigla || c.nome} — ${dataIta(c.avviata_il)}`))),
    },
    {
        tipo: 'richiesta_accesso', etichetta: 'Una richiesta di autorizzazione',
        clinico: true, vuoleId: true,
        /* La rotta torna due elenchi, non uno: `da_decidere` (quelle che
           tocca a me autorizzare) e `mie` (quelle che ho chiesto io).
           Servono tutte e due, e non si sovrappongono mai - nessuno chiede
           a se' stesso. Provata la forma prima di scriverla: la prima
           versione cercava `richieste`, che non esiste. */
        carica: () => visiteAPI.richiesteAccesso().then((r) => {
            const d = r.data?.data || {};
            return [...(d.da_decidere || []), ...(d.mie || [])].map((x) => riga(
                x.id,
                [dataIta(x.created_at),
                 [x.paziente_cognome, x.paziente_nome].filter(Boolean).join(' '),
                 [x.richiedente_cognome, x.richiedente_nome].filter(Boolean).join(' '),
                ].filter(Boolean).join(' — ')));
        }),
    },

    // --- fuori dalla scheda: riguardano la struttura ---
    {
        tipo: 'lotto', etichetta: 'Un lotto di magazzino', vuoleId: true,
        carica: () => api.get('/magazzino/lotti').then((r) =>
            (r.data?.data?.lotti || []).map((l) => riga(
                l.id, `${l.nome || l.prodotto || ''} — lotto ${l.codice_lotto || '—'}`))),
    },
    {
        tipo: 'fattura_ts', etichetta: 'Una fattura nel Sistema TS', vuoleId: true,
        carica: () => api.get('/fatturazione?per_pagina=50').then((r) =>
            (r.data?.data?.fatture || []).map((f) => riga(f.id, etichettaFattura(f)))),
        nota: 'Stessa fattura, ma si apre nella schermata del Sistema TS.',
    },
    {
        tipo: 'resoconto_ts', etichetta: 'Un resoconto del Sistema TS', vuoleId: true,
        carica: () => api.get('/sistema-ts/resoconti').then((r) =>
            (r.data?.data?.resoconti || []).map((x) => riga(
                x.id, `${dataIta(x.periodo_dal)} – ${dataIta(x.periodo_al)} (${x.esito || 'in corso'})`))),
    },
];

const perTipo = (t) => TIPI.find((x) => x.tipo === t);

const RiferimentoSelect = ({ pazienteId, tipo, id, onChange, classe = '' }) => {
    const [elenco, setElenco] = useState([]);
    const [caricando, setCaricando] = useState(false);
    const [errore, setErrore] = useState(false);

    const scelto = perTipo(tipo);

    /* L'elenco si carica solo quando serve davvero: tipo scelto, e tipo
       che vuole un identificativo. Fuori da qui non si chiede niente al
       server. */
    useEffect(() => {
        const t = perTipo(tipo);
        if (!t || !t.vuoleId) { setElenco([]); setErrore(false); return; }
        if (t.clinico && !pazienteId) { setElenco([]); return; }

        let attivo = true;
        setCaricando(true);
        setErrore(false);
        Promise.resolve(t.carica(pazienteId))
            .then((righe) => { if (attivo) setElenco(righe || []); })
            .catch(() => { if (attivo) { setElenco([]); setErrore(true); } })
            .finally(() => { if (attivo) setCaricando(false); });
        return () => { attivo = false; };
    }, [tipo, pazienteId]);

    /* Cambiando paziente, una destinazione clinica gia' scelta non vale
       piu': indicherebbe la visita di un altro. */
    useEffect(() => {
        const t = perTipo(tipo);
        if (t && t.clinico && !pazienteId) onChange(null, null);
        /* eslint-disable-next-line */
    }, [pazienteId]);

    const disponibili = TIPI.filter((t) => !t.clinico || pazienteId);
    const clinici = disponibili.filter((t) => t.clinico);
    const struttura = disponibili.filter((t) => !t.clinico);

    return (
        <div className={`rif-campo ${classe}`}>
            <label>
                <span>Collega a</span>
                <select value={tipo || ''}
                        onChange={(e) => onChange(e.target.value || null, null)}>
                    <option value="">Niente in particolare</option>
                    {clinici.length > 0 && (
                        <optgroup label="Nella scheda del paziente">
                            {clinici.map((t) => (
                                <option key={t.tipo} value={t.tipo}>{t.etichetta}</option>
                            ))}
                        </optgroup>
                    )}
                    <optgroup label="Nella struttura">
                        {struttura.map((t) => (
                            <option key={t.tipo} value={t.tipo}>{t.etichetta}</option>
                        ))}
                    </optgroup>
                </select>
            </label>

            {scelto?.vuoleId && (
                <label>
                    <span>Quale</span>
                    <select value={id || ''}
                            disabled={caricando || elenco.length === 0}
                            onChange={(e) => onChange(tipo, e.target.value
                                ? parseInt(e.target.value, 10) : null)}>
                        <option value="">
                            {caricando ? 'Carico…'
                                : elenco.length === 0 ? 'Niente da scegliere'
                                : 'Scegli…'}
                        </option>
                        {elenco.map((r) => (
                            <option key={r.id} value={r.id}>{r.etichetta}</option>
                        ))}
                    </select>
                </label>
            )}

            {scelto?.nota && <small className="rif-nota">{scelto.nota}</small>}
            {errore && (
                <small className="rif-nota">
                    Non riesco a leggere l&apos;elenco. La destinazione resta senza dettaglio.
                </small>
            )}
            {scelto?.vuoleId && !caricando && !errore && elenco.length === 0 && (
                <small className="rif-nota">
                    Non c&apos;è niente di questo tipo da collegare.
                </small>
            )}
        </div>
    );
};

export default RiferimentoSelect;
